import React, { useEffect, useState } from 'react';
import {
  Box, Button, Card, CardContent, Chip, FormControl, InputLabel, MenuItem, Select,
  Typography, IconButton, Alert, CircularProgress, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper, TablePagination, Tooltip
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Visibility as ViewIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import millingApi, { MillingCostConfig, CostType } from '../../services/millingApi';

const costTypeColors: Record<CostType, 'primary' | 'secondary' | 'info' | 'warning' | 'success' | 'error' | 'default'> = {
  labor: 'primary',
  overhead: 'secondary',
  material: 'info',
  depreciation: 'warning',
  utilities: 'success',
  maintenance: 'error',
  other: 'default',
};

const MillingCostConfigList: React.FC = () => {
  const navigate = useNavigate();
  const [configs, setConfigs] = useState<MillingCostConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterCostType, setFilterCostType] = useState<string>('');
  const [filterActive, setFilterActive] = useState<string>('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    fetchConfigs();
  }, [filterCostType, filterActive, page, rowsPerPage]);

  const fetchConfigs = async () => {
    try {
      setLoading(true);
      const params: any = {};
      if (filterCostType) params.costType = filterCostType;
      if (filterActive) params.isActive = filterActive === 'active';
      const response = await millingApi.getCostConfigs(params);
      setConfigs(response.data || response);
    } catch (err) {
      console.error(err);
      setError('Error loading cost configurations');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this cost configuration?')) return;
    try {
      await millingApi.deleteCostConfig(id);
      fetchConfigs();
    } catch (err) {
      alert('Error deleting cost configuration');
    }
  };

  if (loading && configs.length === 0) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Milling Cost Configurations</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/milling/costs/new')}>
          New Cost Config
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Cost Type</InputLabel>
              <Select value={filterCostType} label="Cost Type" onChange={(e) => setFilterCostType(e.target.value)}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="labor">Labor</MenuItem>
                <MenuItem value="overhead">Overhead</MenuItem>
                <MenuItem value="material">Material</MenuItem>
                <MenuItem value="depreciation">Depreciation</MenuItem>
                <MenuItem value="utilities">Utilities</MenuItem>
                <MenuItem value="maintenance">Maintenance</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Status</InputLabel>
              <Select value={filterActive} label="Status" onChange={(e) => setFilterActive(e.target.value)}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </CardContent>
      </Card>

      <Card>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Config Code</TableCell>
                <TableCell>Config Name</TableCell>
                <TableCell>Cost Type</TableCell>
                <TableCell align="right">Rate/Unit</TableCell>
                <TableCell align="right">Rate/Hour</TableCell>
                <TableCell align="right">Fixed Cost</TableCell>
                <TableCell>Allocation</TableCell>
                <TableCell align="center">Default</TableCell>
                <TableCell align="center">Active</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {configs.map((config) => (
                <TableRow key={config.id} hover>
                  <TableCell><Typography fontWeight="bold">{config.configCode}</Typography></TableCell>
                  <TableCell>{config.configName}</TableCell>
                  <TableCell>
                    <Chip label={config.costType} color={costTypeColors[config.costType]} size="small" />
                  </TableCell>
                  <TableCell align="right">{config.ratePerUnit ? `$${config.ratePerUnit.toFixed(2)}` : '-'}</TableCell>
                  <TableCell align="right">{config.ratePerHour ? `$${config.ratePerHour.toFixed(2)}` : '-'}</TableCell>
                  <TableCell align="right">{config.fixedCost ? `$${config.fixedCost.toFixed(2)}` : '-'}</TableCell>
                  <TableCell>{config.allocationMethod.replace('_', ' ')}</TableCell>
                  <TableCell align="center">{config.isDefault ? <Chip label="Yes" size="small" color="primary" /> : '-'}</TableCell>
                  <TableCell align="center">
                    <Chip label={config.isActive ? 'Yes' : 'No'} size="small" color={config.isActive ? 'success' : 'default'} />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="View"><IconButton size="small" onClick={() => navigate(`/milling/costs/${config.id}`)}><ViewIcon /></IconButton></Tooltip>
                    <Tooltip title="Edit"><IconButton size="small" onClick={() => navigate(`/milling/costs/${config.id}/edit`)}><EditIcon /></IconButton></Tooltip>
                    <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => handleDelete(config.id!)}><DeleteIcon /></IconButton></Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {configs.length === 0 && (
                <TableRow><TableCell colSpan={10} align="center">No cost configurations found</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={configs.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        />
      </Card>
    </Box>
  );
};

export default MillingCostConfigList;
